import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  FormControlLabel,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledSwitch from "components/Styled/Switch";
import StyledVaPage from "components/Styled/VAPage";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import { fetcherAuth } from "utils/api";
import { MODIFIED_EMAIL_WARMUP } from "utils/localStorage";
import { useTracking } from "utils/useTracking";

import LineChart from "./LineChart";

interface Props {
  finishSetUpLater: () => void;
}

const SelfServeVaEmailWarmup = ({ finishSetUpLater }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, organizationVaProfile, setOrganizationVaProfile } =
    useContext(OrganizationUserContext);

  const navigate = useNavigate();
  const { trackVaStep } = useTracking();

  const hasModifiedEmailWarmup =
    localStorage.getItem(`${MODIFIED_EMAIL_WARMUP}-${currentOrg?.id}`) ===
    "true";

  const [emailWarmupEnabled, setEmailWarmupEnabled] = useState(
    hasModifiedEmailWarmup ? organizationVaProfile?.emailWarmupEnabled : true,
  );
  const [confirmWarningDialog, setConfirmWarningDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!emailWarmupEnabled) {
      setConfirmWarningDialog(true);
      return;
    }
    await saveAndContinue(emailWarmupEnabled);
  };

  const handleConfirmSkipWarmup = (enabled: boolean) => {
    setConfirmWarningDialog(false);
    saveAndContinue(enabled);
  };

  const saveAndContinue = async (warmupEnabled: boolean) => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/va-settings`,
        "PUT",
        {},
        { emailWarmupEnabled: warmupEnabled },
      );
      setOrganizationVaProfile((prev) =>
        prev ? { ...prev, emailWarmupEnabled: warmupEnabled } : prev,
      );
      localStorage.setItem(
        `${MODIFIED_EMAIL_WARMUP}-${currentOrg?.id}`,
        "true",
      );
      navigate(`/${routes.connectGmailSelfServeOnboarding}`);
      trackVaStep(routes.connectGmailSelfServeOnboarding);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StyledVaPage
        stepNumber={5}
        title="Email Warmup 🔥"
        time="1"
        description="Protect your sender reputation and maximize deliverability"
        body={
          <Grid
            container
            direction="column"
            alignItems="center"
            gap={2}
            sx={{ mb: 3 }}
          >
            <QuestionBox>
              <Grid container justifyContent="center">
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <StyledSwitch
                      checked={emailWarmupEnabled}
                      onChange={(e) => setEmailWarmupEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
                      {emailWarmupEnabled
                        ? "Yes, warm up my email"
                        : "Skip email warm up"}
                    </Typography>
                  }
                />
              </Grid>

              <Typography>
                {!emailWarmupEnabled ? (
                  <>
                    ⚠️ Keep in mind: Skipping warm-up may increase the risk of
                    emails going to spam, especially with newer email accounts.
                  </>
                ) : (
                  <>
                    ✨ Bento AI will start by sending 50-60 emails per week,
                    then gradually increase to 100 pitches per week.
                  </>
                )}
              </Typography>
            </QuestionBox>

            <Box
              sx={{
                p: 3,
                backgroundColor: "purple.light",
                borderRadius: 4,
                width: "100%",
                maxWidth: 600,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2.5 }}>
                Why warm up your email?
              </Typography>

              {[
                {
                  icon: "fa-shield-halved",
                  title: "Avoid spam folders",
                  detail:
                    "Email providers trust senders who gradually increase their send volume",
                },
                {
                  icon: "fa-envelope-open-text",
                  title: "Higher response rate",
                  detail:
                    "Brands are more likely to see and respond to emails that land in their inbox",
                },
                {
                  icon: "fa-chart-line",
                  title: "Long-term account health",
                  detail:
                    "Build a strong sender reputation that improves deliverability over time",
                },
              ].map((item) => (
                <Box
                  key={item.title}
                  sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      backgroundColor: "purple.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mr: 1.5,
                    }}
                  >
                    <Box
                      component="i"
                      className={`fa-solid ${item.icon}`}
                      sx={{ color: "white", fontSize: 15 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, color: grey[700], mt: 0.3 }}
                    >
                      {item.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Line chart */}
              {!isMobileScreen && <LineChart />}
            </Box>
          </Grid>
        }
        onBack={() => {
          trackVaStep(routes.preferencesSelfServeOnboarding);
          navigate(`/${routes.preferencesSelfServeOnboarding}`);
        }}
        buttonBack={"Back"}
        buttonNext={"Continue to Gmail Connect"}
        onNext={handleSubmit}
        onFinishSetUpLater={finishSetUpLater}
        loading={loading}
      />

      <ConfirmDialog
        open={confirmWarningDialog}
        header="Are you sure you want to skip email warmup?"
        subheader="Skipping warm-up may increase the risk of emails going to spam, especially with newer email accounts."
        handleClose={() => setConfirmWarningDialog(false)}
        handleConfirm={() => handleConfirmSkipWarmup(true)}
        handleCancel={() => handleConfirmSkipWarmup(false)}
        confirmText="Warm up my email"
        cancelText="Leave as is"
      />
    </>
  );
};

export default SelfServeVaEmailWarmup;
